import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/data/context";
import { deviceId, getStored, setStored } from "@/lib/storage";
import { openTask } from "@/features/ui-state";
import { NotificationTimers } from "./scheduler";

const POLL_MS = 60_000;
/** Data changes (live events, local edits) resync at most this often. */
const CHANGE_THROTTLE_MS = 3_000;
const SYNC_EVENT = "desk:notifications-sync";

/** Ask the browser scheduler to resync now, e.g. right after notification permission is granted. */
export function requestNotificationSync() {
  window.dispatchEvent(new Event(SYNC_EVENT));
}

/**
 * Browser notifications while the tab is open: keep the server's future schedule armed as timers
 * (if permission was granted by a deliberate click in Settings), fire each at its time, then ack.
 * Resyncs every minute, after data changes, and when the tab regains focus or wakes.
 * In Electron (`window.deskBridge`) the main process presents native notifications instead.
 */
export function useWebNotifications() {
  const api = useApi();
  const qc = useQueryClient();
  useEffect(() => {
    if (window.deskBridge || typeof Notification === "undefined") return;
    const device = deviceId();
    const timers = new NotificationTimers({
      fetch: async () => (await api.notificationSchedule(device)).items,
      ack: (id) => api.ackNotification(id),
      allowed: () => Notification.permission === "granted",
      loadFired: () => getStored<string[]>("notifications.fired", []),
      saveFired: (ids) => setStored("notifications.fired", ids),
      present: (n) => {
        const note = new Notification(n.title, { body: n.body, tag: n.id });
        note.onclick = () => {
          window.focus();
          openTask(n.taskId, "overlay");
        };
      },
    });
    const sync = () => void timers.sync();
    const wake = () => {
      if (document.visibilityState !== "visible") return;
      timers.fireDue();
      sync();
    };
    let lastChange = 0;
    let changeTimer: ReturnType<typeof setTimeout> | null = null;
    const onChange = () => {
      if (changeTimer) return;
      const wait = Math.max(0, lastChange + CHANGE_THROTTLE_MS - Date.now());
      changeTimer = setTimeout(() => {
        changeTimer = null;
        lastChange = Date.now();
        sync();
      }, wait);
    };
    const unsubscribeCache = qc.getQueryCache().subscribe((e) => {
      if (e.type === "updated" && (e.action.type === "invalidate" || e.action.type === "success")) onChange();
    });
    let permissionStatus: PermissionStatus | null = null;
    void navigator.permissions
      ?.query({ name: "notifications" as PermissionName })
      .then((status) => {
        permissionStatus = status;
        status.onchange = sync;
      })
      .catch(() => {});

    sync();
    const interval = setInterval(sync, POLL_MS);
    window.addEventListener(SYNC_EVENT, sync);
    window.addEventListener("focus", wake);
    window.addEventListener("online", sync);
    document.addEventListener("visibilitychange", wake);
    return () => {
      timers.stop();
      clearInterval(interval);
      if (changeTimer) clearTimeout(changeTimer);
      unsubscribeCache();
      if (permissionStatus) permissionStatus.onchange = null;
      window.removeEventListener(SYNC_EVENT, sync);
      window.removeEventListener("focus", wake);
      window.removeEventListener("online", sync);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [api, qc]);
}
